/* sugar:assets/product-info.js@2.0.0 */
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      // When the picked option combination has no variant (sparse variant
      // matrices), resolve the nearest variant carrying the just-changed
      // value BEFORE rendering, so the page swaps once instead of flashing
      // an intermediate unavailable/first-variant state. Uses the variant
      // data the picker embeds, so no network round-trip is needed.
      resolveMissingVariantFallback() {
        const variantSelects = this.variantSelectors;
        const changed = this.lastChangedOption;
        if (!variantSelects || !changed || !Number.isFinite(changed.index)) return null;

        let variants, current;
        try {
          variants = JSON.parse(variantSelects.querySelector('[data-product-variants]')?.textContent || 'null');
          current = JSON.parse(variantSelects.querySelector('[data-selected-variant]')?.textContent || 'null');
        } catch (error) {
          return null;
        }
        if (!Array.isArray(variants) || !variants.length || !Array.isArray(current?.options)) return null;

        // Predict the combination the section render will resolve: the live
        // value of every rendered option holder, and for options with no
        // holder on the page the server falls back to the option's first
        // value (NOT the currently selected one).
        const requested = (variants[0].options || []).slice();
        variantSelects.querySelectorAll('[data-option-position]').forEach((holder) => {
          const position = parseInt(holder.dataset.optionPosition, 10);
          const value = holder.querySelector('input:checked')?.value ?? holder.querySelector('select')?.value;
          if (Number.isFinite(position) && position < requested.length && value != null) {
            requested[position] = value;
          }
        });
        requested[changed.index] = changed.value;

        const exact = variants.find(
          (variant) => Array.isArray(variant.options) && variant.options.every((opt, i) => opt === requested[i])
        );
        if (exact) return null;

        const candidates = variants.filter((variant) => variant.options?.[changed.index] === changed.value);
        if (!candidates.length) return null;

        // Prefer available variants, then the one keeping most of the
        // currently selected options.
        const matchScore = (variant) =>
          variant.options.reduce(
            (score, opt, i) => score + (i !== changed.index && opt === current.options[i] ? 1 : 0),
            0
          );
        return candidates.slice().sort(
          (a, b) => (b.available === a.available ? 0 : b.available ? 1 : -1) || matchScore(b) - matchScore(a)
        )[0];
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const optionHolder = event.target.closest?.('[data-option-name]');
        this.lastChangedOption = optionHolder
          ? {
              name: optionHolder.dataset.optionName,
              value: event.target.value,
              index: parseInt(optionHolder.dataset.optionPosition, 10),
            }
          : null;

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        let requestUrl = this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage);
        if (!shouldSwapProduct) {
          const fallbackVariant = this.resolveMissingVariantFallback();
          if (fallbackVariant) {
            requestUrl = `${productUrl.split('?')[0]}?variant=${fallbackVariant.id}&section_id=${this.sectionId}`;
          }
        }

        this.renderProductInfo({
          requestUrl,
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          // The section's Anchor ID replaces its MainProduct id, so find it by attribute.
          const selector = updateFullPage ? 'product-info[data-main-product]' : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value, without scrolling to it: the pick may
            // have come from a sticky bar or drawer while the picker is off screen
            if (targetId) document.getElementById(targetId)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          // The variant picker owns its own markup and state (assets/variant-picker.js);
          // replacing it from the section render would drop its listeners and any
          // popup dialog wired inside it.
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            if (this.tryNearestVariantFallback(productUrl)) return;
            this.setUnavailable();
            return;
          }
          this.nearestFallbackAttempted = false;

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.className = source.className;
              const sourceStyle = source.getAttribute('style');
              if (sourceStyle !== null) {
                destination.setAttribute('style', sourceStyle);
              } else {
                destination.removeAttribute('style');
              }
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('InventoryPulse');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          this.syncPriceSoldOutState(variant);

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector('.quantity__rules')?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const sourceSubmitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          const sourceSubmitText = sourceSubmitButton?.querySelector('[data-submit-text], .buy-button__text, span');
          if (variant.available && sourceSubmitText?.innerHTML) {
            this.productForm?.setSubmitButtonDefaultText(sourceSubmitText.innerHTML);
          }

          const isSoldOut = variant.available === false;
          this.productForm?.toggleSubmitButton(isSoldOut, isSoldOut ? window.variantStrings.soldOut : undefined);

          const variantChangeData = {
            sectionId: this.sectionId,
            html,
            variant,
          };

          try {
            const publishResult = publish(PUB_SUB_EVENTS.variantChange, {
              data: variantChangeData,
            });
            if (publishResult?.catch) {
              publishResult.catch((error) => console.error(error));
            }
          } catch (error) {
            console.error(error);
          }

          // Backward compatibility for custom blocks that still listen for a DOM event.
          document.dispatchEvent(
            new CustomEvent('variant:change', {
              detail: variantChangeData,
            })
          );
        };
      }

      // When the picked option combination matches no variant (e.g. a colour
      // sold only in some sizes), jump to the closest variant carrying the
      // newly picked value instead of dead-ending on "Unavailable".
      tryNearestVariantFallback(productUrl) {
        const changed = this.lastChangedOption;
        if (!changed || this.nearestFallbackAttempted) return false;
        this.nearestFallbackAttempted = true;

        fetch(`${productUrl.split('?')[0]}.js`)
          .then((response) => response.json())
          .then((product) => {
            const optionIndex = product.options.findIndex((option) => option.name === changed.name);
            if (optionIndex < 0) return this.setUnavailable();
            const candidates = product.variants.filter((v) => v.options[optionIndex] === changed.value);
            const fallback = candidates.find((v) => v.available) || candidates[0];
            if (!fallback) return this.setUnavailable();
            this.renderProductInfo({
              requestUrl: `${this.dataset.url}?variant=${fallback.id}&section_id=${this.sectionId}`,
              targetId: '',
              callback: this.handleUpdateProductInfo(this.dataset.url),
            });
          })
          .catch(() => this.setUnavailable());
        return true;
      }

      updateVariantInputs(variantId) {
        /* The installment form is addressed by class too: a block-scoped one carries its own id. */
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, form.installment`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'InventoryPulse', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      syncPriceSoldOutState(variant) {
        const isSoldOut = variant?.available === false;
        this.querySelectorAll('.sugar-product-price .price').forEach((priceEl) => {
          priceEl.classList.toggle('price--sold-out', isSoldOut);
        });
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.querySelector('.product-form__quantity');
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
